import { Box, ResponsiveContext, Text, ThemeContext } from 'grommet';
import React, { useContext, useRef, useState } from 'react';
import { IconMailFast, IconPlus, IconX as IconClose } from '@tabler/icons-react';
import useSWR from 'swr';
import { useSession } from 'next-auth/react';
import Section from '../../Section';
import Button from '../../Button';
import DropButton from '../../DropButton';
import Icon from '../../Icon';
import StatusIcon from '../../StatusIcon';
import EmailTagInput from './EmailTagInput';
import { NotificationsContext } from '../../NotificationsProvider';
import { handleErrors } from '../../../utils/errors';
import { formatName } from '../../../utils/names';
import { validateEmail } from '../../../lib/validation';
import { DROP_MAX_WIDTH } from '../../../utils/theme';

const ENDPOINT = '/api/company/notification-subscriptions';
const EVENT_SECTION = 'settings_notification_subscriptions';

const sameEmail = (a, b) => (a || '').trim().toLowerCase() === (b || '').trim().toLowerCase();

const Chip = ({ recipient, isMe, canRemove, disabled, onRemove }) => {
    // light-1 is a flat Grommet base colour with no dark variant, so on its own it puts
    // light text on a near-white chip in dark mode. alice-blue already has both.
    const theme = useContext(ThemeContext);

    return (
        <Box
            direction="row"
            align="center"
            gap="xsmall"
            round="xxsmall"
            pad={{ vertical: 'xxsmall', horizontal: 'xsmall' }}
            background={isMe ? 'alice-blue' : (theme.dark ? 'foreground' : 'light-1')}
            border={isMe ? undefined : { color: 'border' }}
            flex={false}
            style={{ maxWidth: '220px' }}
            title={recipient.email}>
            <Text size="small" truncate style={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                {`${formatName(recipient, true) || recipient.email}${isMe ? ' (Me)' : ''}`}
            </Text>
            {canRemove && (
                <Button
                    plain
                    disabled={disabled}
                    a11yTitle={`Remove ${recipient.email}`}
                    event={{ section: EVENT_SECTION, element: 'remove_recipient' }}
                    icon={<Icon SelectedIcon={IconClose} color="text-weak" size="xxxsmall" />}
                    onClick={onRemove} />
            )}
        </Box>
    );
};

// Only the three fields the API stores. A teammate picked from the suggestions arrives as
// a whole user record, and there is no reason to put their permissions on the wire.
const toRecipient = ({ email, first_name, last_name }) => ({ email, first_name, last_name });

// The round plus the app uses wherever something gets added
const addIcon = <Icon SelectedIcon={IconPlus} size="small" />;

const AddRecipients = ({ notification, team, disabled, onAdd }) => {
    const isPhone = useContext(ResponsiveContext) === 'small';
    const [open, setOpen] = useState(false);
    const [tags, setTags] = useState([]);
    const [keyword, setKeyword] = useState('');
    const [hasValidationError, setHasValidationError] = useState(false);
    const [inlineError, setInlineError] = useState();

    const typed = keyword.trim();
    const typedIsEmail = !!typed && typeof validateEmail(typed) === 'undefined';
    const candidates = [...tags, ...(typedIsEmail ? [{ email: typed }] : [])];
    const fresh = candidates.filter((c, i) =>
        !notification.recipients.some(r => sameEmail(r.email, c.email)) &&
        candidates.findIndex(o => sameEmail(o.email, c.email)) === i);

    const canAdd = !disabled && !hasValidationError && fresh.length > 0;

    const close = () => {
        setOpen(false);
        setTags([]);
        setKeyword('');
    };

    // Closing first threw away what the user typed whenever the save failed, and they were
    // told "try again" with nothing left to try again with. The drop stays open until the
    // write comes back, so a 422 lands next to the addresses that caused it.
    const add = async () => {
        if (!fresh.length) return close();
        const saved = await onAdd(fresh.map(toRecipient));
        if (saved) close();
    };

    return (
        <span style={{ display: 'inline-flex', flexShrink: 0 }}>
            <DropButton
                event={{ section: EVENT_SECTION, element: 'add_recipient' }}
                icon={addIcon}
                round
                hoverIndicator
                disabled={disabled}
                open={open}
                onOpen={() => setOpen(true)}
                onClose={close}
                a11yTitle={`Add recipient to ${notification.name}`}
                dropAlign={{ top: 'bottom', right: 'right' }}
                // A phone pins the drop to the screen's left edge, so give it the page's gutter
                dropProps={isPhone ? { margin: { left: '12px' } } : undefined}
                dropContent={(
                    // 400px, or the screen less its gutters on a phone, where 400px ran off the left
                    <Box pad="small" gap="small" style={{ width: `min(400px, ${DROP_MAX_WIDTH})` }}>
                        <Text size="small" color="text-weak">Pick a teammate, or type any email address.</Text>
                        {/* The outline every other input has; a plain border is the faint divider colour */}
                        <Box border={{ color: 'border-strong', size: 'xsmall' }} round="xsmall">
                            <EmailTagInput
                                value={[]}
                                // Nobody already on the row is worth suggesting
                                team={{ ...team, users: (team?.users || []).filter(u => !notification.recipients.some(r => sameEmail(r.email, u.email))) }}
                                setHasEmailValidationError={setHasValidationError}
                                onKeywordChange={setKeyword}
                                setInlineError={setInlineError}
                                // Enter does what Add does, taking an address still being typed too
                                onSubmit={() => canAdd && add()}
                                onEmailChange={e => setTags(e.value)} />
                        </Box>
                        {(hasValidationError || inlineError) && (
                            <Box direction="row" align="center" gap="xsmall">
                                <StatusIcon type="error" size="xsmall" />
                                <Text size="small" color="status-critical">
                                    {inlineError || 'Please check the email addresses entered.'}
                                </Text>
                            </Box>
                        )}
                        <Box align="end">
                            <Button
                                primary
                                label="Add"
                                a11yTitle={`Add to ${notification.name}`}
                                event={{ section: EVENT_SECTION, element: 'save_recipients', description: notification.key }}
                                disabled={!canAdd}
                                onClick={add} />
                        </Box>
                    </Box>
                )}
            />
        </span>
    );
};

const NotificationSubscriptionSection = ({ team, canManageOthers }) => {
    const { data: session } = useSession();
    const notificationCtx = useContext(NotificationsContext);
    const isPhone = useContext(ResponsiveContext) === 'small';
    const [busy, setBusy] = useState(false);
    // Every write rebuilds a whole list from the rendered snapshot, so a second
    // write while one is in flight would be built from stale data and undo it.
    // State alone can lag a click, so the ref is the real guard.
    const inFlight = useRef(false);

    const { data, mutate } = useSWR(
        ENDPOINT,
        (url) => fetch(url)
            .then(response => handleErrors(response, { redirectOnError: false, signOutOnUnauthorized: true }))
            .catch(() => notificationCtx.showNotification({ type: 'error', message: "We aren't able to fetch your settings. Please refresh the page and try again." })),
        { revalidateOnFocus: false }
    );

    const notifications = Array.isArray(data) ? data : [];
    const viewerEmail = session?.user?.email;
    const isMe = (recipient) => sameEmail(recipient.email, viewerEmail);
    const mine = notifications.filter(n => n.recipients.some(isMe)).map(n => n.key);
    const onEverything = notifications.length > 0 && mine.length === notifications.length;

    // Returns whether the write landed, so the add drop knows whether to close.
    const put = async (url, body) => {
        if (inFlight.current) return false;
        inFlight.current = true;
        setBusy(true);
        try {
            await fetch(url, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            // A 403 or 404 on a write belongs in the toast beside the control, not in a
            // navigation away from the page the toast would have appeared on. A dead
            // session is still a sign-out.
            }).then(response => handleErrors(response, { redirectOnError: false, signOutOnUnauthorized: true }));
            await mutate();
            return true;
        } catch (error) {
            notificationCtx.showNotification({
                type: 'error',
                message: error?.error || "We couldn't save that change. Please try again.",
            });
            return false;
        } finally {
            inFlight.current = false;
            setBusy(false);
        }
    };

    const saveMine = (subscribed_keys) => put(`${ENDPOINT}/me`, { subscribed_keys });
    const saveRecipients = (key, recipients) => put(ENDPOINT, { key, recipients });

    // One button that flips, since two side by side wrapped onto two lines on a phone
    const subscribeAll = onEverything ? (
        <Button
            secondary
            label="Unsubscribe from all"
            disabled={busy}
            event={{ section: EVENT_SECTION, element: 'unsubscribe_all' }}
            onClick={() => saveMine([])} />
    ) : (
        <Button
            secondary
            label="Subscribe to all"
            disabled={busy || !data}
            event={{ section: EVENT_SECTION, element: 'subscribe_all' }}
            onClick={() => saveMine(notifications.map(n => n.key))} />
    );

    return (
        <Section
            title="Email notifications"
            // It was briefly titled Notifications
            legacyAnchorIds={['notifications']}
            icon={IconMailFast}
            // In the header beside the title, except on a phone: there the full-size button
            // crowded the title, so it sits under the list instead
            control={isPhone ? undefined : subscribeAll}
            // Rows pad themselves, so no side padding: their dividers run the
            // full width like the section header's
            contentProps={{ pad: { bottom: 'xsmall' } }}
        >
            {notifications.map((notification) => (
                <Box
                    key={notification.key}
                    data-testid={`notification-${notification.key}`}
                    direction="row-responsive"
                    gap="small"
                    align={isPhone ? 'stretch' : 'center'}
                    border={{ side: 'bottom' }}
                    flex={false}
                    pad="small">
                    {/* A fixed column, so every row's recipients start in the same place. Sized
                        to the content, the name was squeezed onto two lines by a long list. */}
                    <Box flex={false} width={isPhone ? undefined : '220px'}>
                        <Text size="17px" weight={500}>{notification.name}</Text>
                    </Box>
                    {/* Shares the row with the name, except on a phone where it sits under it */}
                    <Box direction="row" gap="small" align="center" {...(isPhone ? { flex: false } : { flex: 'grow', basis: '0' })}>
                        {/* Recipients wrap onto as many lines as they need, left aligned, while the
                            add button keeps its own column at the end instead of trailing the last chip */}
                        <Box direction="row" align="center" justify="start" wrap flex="grow" basis="0" style={{ gap: '6px' }}>
                            {notification.recipients.length === 0 && (
                                <Text size="small" color="text-weak">No recipients</Text>
                            )}
                            {notification.recipients.map((recipient) => {
                                const me = isMe(recipient);
                                return (
                                    <Chip
                                        key={recipient.email}
                                        recipient={recipient}
                                        isMe={me}
                                        canRemove={me || canManageOthers}
                                        disabled={busy}
                                        onRemove={() => me
                                            ? saveMine(mine.filter(k => k !== notification.key))
                                            : saveRecipients(notification.key, notification.recipients.filter(r => r !== recipient).map(toRecipient))} />
                                );
                            })}
                        </Box>
                        {!canManageOthers && !notification.recipients.some(isMe) && (
                            // Members without settings access can add themselves, never anyone else
                            <Button
                                icon={addIcon}
                                round
                                hoverIndicator
                                tip="Add me"
                                disabled={busy}
                                a11yTitle="Add me"
                                event={{ section: EVENT_SECTION, element: 'add_me', description: notification.key }}
                                onClick={() => saveMine([...mine, notification.key])} />
                        )}
                        {canManageOthers && (
                            <AddRecipients
                                notification={notification}
                                team={team}
                                disabled={busy}
                                onAdd={(added) => saveRecipients(notification.key, [...notification.recipients.map(toRecipient), ...added])} />
                        )}
                    </Box>
                </Box>
            ))}
            {isPhone && notifications.length > 0 && (
                <Box pad={{ horizontal: 'small', top: 'medium' }}>{subscribeAll}</Box>
            )}
        </Section>
    );
};

export default NotificationSubscriptionSection;
